import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import CyberLayout from '@/components/CyberLayout';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

// Import refactored components
import {
  DateInput,
  NumberInput,
  PlateInput,
  VehicleInput,
  EmailInput,
  PhoneInput,
  TransportSelect,
  BullyingSelect,
  YesNoSelect,
  TimeInput,
  SuspectedLoverForm,
  SocialProfilesForm,
  WorkplaceInput,
  AddressInput,
  TextInput,
  ValidationWarning,
  QuestionHeader,
  ProgressBar,
  QuestionActions,
  AnsweredCount
} from '@/components/questionnaire';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Questionnaire() {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  
  // State
  const [selectedType, setSelectedType] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [isTyping, setIsTyping] = useState(true);
  const [isRequired, setIsRequired] = useState(true);
  const [questionType, setQuestionType] = useState('text');
  const [vehicleMode, setVehicleMode] = useState(null);
  const [validationWarning, setValidationWarning] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [fieldCity, setFieldCity] = useState('');
  const [history, setHistory] = useState([]);

  const pushHistory = () => setHistory(h => [...h, {
    question: currentQuestion, type: questionType, vehicleMode, isRequired, index: questionIndex
  }]);

  // Initialize from session storage
  useEffect(() => {
    const privacyAccepted = sessionStorage.getItem('privacyAccepted');
    const typeData = sessionStorage.getItem('selectedType');
    
    if (!privacyAccepted || !typeData) {
      navigate('/select-type');
      return;
    }
    
    const type = JSON.parse(typeData);
    setSelectedType(type);
    const baseQuestions = type[`base_questions_${language}`] || type.base_questions_it;
    setTotalQuestions(baseQuestions.length + 3);
    
    // Check if returning to edit - restore previous answers
    const savedAnswers = sessionStorage.getItem('answers');
    if (savedAnswers) {
      const prevAnswers = JSON.parse(savedAnswers);
      setAnswers(prevAnswers);
      // Start from beginning but with answers pre-loaded so user can review/edit
      fetchNextQuestion(type.id, 0, prevAnswers);
    } else {
      fetchNextQuestion(type.id, 0, {});
    }
  }, [navigate, language]);

  // Fetch next question from API
  const fetchNextQuestion = async (typeId, index, prevAnswers, skipConditional = false) => {
    setLoading(true);
    setIsTyping(true);
    try {
      const response = await axios.post(`${API}/ai/generate-question`, {
        investigation_type_id: typeId,
        language: language,
        previous_answers: prevAnswers,
        current_question_index: index
      });
      
      if (response.data.is_final || !response.data.question) {
        sessionStorage.setItem('answers', JSON.stringify(prevAnswers));
        navigate('/photos');
        return;
      }
      
      // Check if this is a conditional question (children_details) and should be skipped
      const qType = response.data.question_type || 'text';
      if (qType === 'children_details' && !skipConditional) {
        // Check if previous answer about children was "no"
        const hasChildrenAnswer = Object.entries(prevAnswers).find(([q, a]) => 
          q.toLowerCase().includes('ha figli') || q.toLowerCase().includes('have children')
        );
        
        if (hasChildrenAnswer && hasChildrenAnswer[1].toLowerCase() === 'no') {
          // Skip this question and fetch the next one
          const newIndex = index + 1;
          setQuestionIndex(newIndex);
          fetchNextQuestion(typeId, newIndex, prevAnswers, true);
          return;
        }
      }
      
      setCurrentQuestion(response.data.question);
      setIsRequired(response.data.is_required !== false);
      setQuestionType(qType);
      setVehicleMode(response.data.vehicle_mode || null);
      
      // Pre-fill answer if editing (returning from summary)
      const existingAnswer = prevAnswers[response.data.question];
      if (existingAnswer) {
        setCurrentAnswer(existingAnswer);
      }
      
      setTimeout(() => setIsTyping(false), 500);
    } catch (error) {
      console.error(error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  // Validate workplace answers with AI
  const validateAnswer = async () => {
    if (questionType !== 'workplace') return true;
    
    setIsValidating(true);
    try {
      const response = await axios.post(`${API}/ai/validate-answer`, {
        question: currentQuestion,
        answer: currentAnswer,
        question_type: questionType,
        language: language
      });
      
      if (!response.data.valid && response.data.suggestion) {
        setValidationWarning(response.data.suggestion);
        return false;
      }
      setValidationWarning(null);
      return true;
    } catch (error) {
      console.error('Validation error:', error);
      return true;
    } finally {
      setIsValidating(false);
    }
  };

  // Field-level checks: city for addresses, plate for vehicles
  const getAnswerError = () => {
    const it = language === 'it';
    if (!currentAnswer.trim()) return null;
    if ((questionType === 'address' || questionType === 'workplace') && !fieldCity.trim()) {
      return it ? 'La città è obbligatoria' : 'City is required';
    }
    if (questionType === 'vehicle' && vehicleMode !== 'bicycle' && !/(^|- )(Targa|Plate): /.test(currentAnswer)) {
      return it ? 'Inserisci la targa del veicolo' : 'Please enter the license plate';
    }
    if (questionType === 'suspected_lover') {
      try {
        const d = JSON.parse(currentAnswer);
        if (d.indirizzo?.trim() && !d.citta?.trim()) {
          return it ? 'Indica la città in cui abita il presunto amante' : 'Please enter the city where the suspected lover lives';
        }
      } catch { return null; }
    }
    return null;
  };

  // Handle next button
  const handleNext = async () => {
    if (!currentAnswer.trim() && currentQuestion && isRequired) {
      toast.error(language === 'it' ? 'Questa domanda è obbligatoria' : 'This question is required');
      return;
    }
    
    const answerError = getAnswerError();
    if (answerError) {
      toast.error(answerError);
      return;
    }
    
    // Validate "altro" transport selection
    if (questionType === 'select_transport' && currentAnswer === 'altro:') {
      toast.error(language === 'it' ? 'Specifica quale altro mezzo di trasporto' : 'Please specify the other means of transport');
      return;
    }
    
    // Validate workplace answers
    if (questionType === 'workplace' && !validationWarning) {
      const isValid = await validateAnswer();
      if (!isValid) return;
    }
    
    setValidationWarning(null);
    
    const newAnswers = { ...answers };
    if (currentAnswer.trim()) {
      newAnswers[currentQuestion] = currentAnswer.trim();
    }
    
    setAnswers(newAnswers);
    setCurrentAnswer('');
    pushHistory();
    const newIndex = questionIndex + 1;
    setQuestionIndex(newIndex);
    fetchNextQuestion(selectedType.id, newIndex, newAnswers);
  };

  // Handle skip button
  const handleSkip = () => {
    setCurrentAnswer('');
    pushHistory();
    const newIndex = questionIndex + 1;
    setQuestionIndex(newIndex);
    fetchNextQuestion(selectedType.id, newIndex, answers);
  };

  // Handle back button: restore the previously displayed question with its input type
  const handleBack = () => {
    if (history.length === 0) {
      navigate('/select-type');
      return;
    }
    const prev = history[history.length - 1];
    setHistory(h => h.slice(0, -1));
    setValidationWarning(null);
    setQuestionIndex(prev.index);
    setCurrentQuestion(prev.question);
    setQuestionType(prev.type);
    setVehicleMode(prev.vehicleMode);
    setIsRequired(prev.isRequired);
    setCurrentAnswer(answers[prev.question] || '');
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !/una per riga|one per line/i.test(currentQuestion)) {
      e.preventDefault();
      handleNext();
    }
  };

  // Render the appropriate input based on question type
  const renderInput = () => {
    const inputProps = {
      value: currentAnswer,
      onChange: setCurrentAnswer,
      onKeyDown: handleKeyDown,
      language
    };

    switch (questionType) {
      case 'date':
        return <DateInput {...inputProps} />;
      case 'number':
        return <NumberInput {...inputProps} />;
      case 'plate':
        return <PlateInput {...inputProps} />;
      case 'vehicle':
        return <VehicleInput {...inputProps} mode={vehicleMode || 'full'} isRequired={isRequired} />;
      case 'email':
        return <EmailInput {...inputProps} />;
      case 'phone':
        return <PhoneInput {...inputProps} />;
      case 'time':
        return <TimeInput {...inputProps} />;
      case 'yes_no':
        return <YesNoSelect {...inputProps} />;
      case 'children_details':
        return <TextInput {...inputProps} placeholder={language === 'it' ? 'Es: Marco (12 anni), Giulia (8 anni)...' : 'Ex: Marco (12 years), Giulia (8 years)...'} />;
      case 'select_transport':
        return <TransportSelect {...inputProps} />;
      case 'select_bullying':
        return <BullyingSelect {...inputProps} />;
      case 'suspected_lover':
        return <SuspectedLoverForm {...inputProps} />;
      case 'social_profiles':
        return <SocialProfilesForm {...inputProps} />;
      case 'address':
        return <AddressInput {...inputProps} onCityChange={setFieldCity} />;
      case 'workplace':
        return (
          <WorkplaceInput 
            {...inputProps} 
            onCityChange={setFieldCity}
            validationWarning={validationWarning}
            setValidationWarning={setValidationWarning}
          />
        );
      default:
        return <TextInput {...inputProps} placeholder={t('typeAnswer')} />;
    }
  };

  return (
    <CyberLayout>
      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-20">
        <div className="max-w-2xl w-full">
          {/* Header */}
          <div className="text-center mb-8 fade-in-up">
            <h1 className="font-orbitron text-2xl sm:text-3xl font-bold text-white mb-2 tracking-wider">
              {t('questionnaireTitle')}
            </h1>
            <p className="font-mono text-neon-cyan text-sm">
              {selectedType?.[`name_${language}`] || selectedType?.name_it}
            </p>
          </div>

          {/* Progress */}
          <ProgressBar 
            questionIndex={questionIndex} 
            totalQuestions={totalQuestions}
            language={language}
            t={t}
          />

          {/* Question Card */}
          <div className="cyber-card rounded-lg p-8 mb-8 fade-in-up" style={{ animationDelay: '0.2s' }}>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-10 h-10 text-neon-green animate-spin" />
              </div>
            ) : (
              <>
                <QuestionHeader 
                  questionIndex={questionIndex}
                  isRequired={isRequired}
                  currentQuestion={currentQuestion}
                  isTyping={isTyping}
                  language={language}
                />

                {/* Answer Input */}
                <div className="relative">
                  <div className="absolute left-0 top-0 font-mono text-xs text-neon-cyan/50 -mt-5">
                    {'>'} {language === 'it' ? 'RISPOSTA' : 'ANSWER'}:
                  </div>
                  
                  <div key={currentQuestion}>{renderInput()}</div>
                  
                  <ValidationWarning message={validationWarning} language={language} />
                </div>
              </>
            )}
          </div>

          {/* Actions */}
          <QuestionActions 
            onBack={handleBack}
            onSkip={handleSkip}
            onNext={handleNext}
            loading={loading}
            isValidating={isValidating}
            isRequired={isRequired}
            validationWarning={validationWarning}
            language={language}
            t={t}
          />

          {/* Answered Count */}
          <AnsweredCount count={Object.keys(answers).length} language={language} />
        </div>
      </div>
    </CyberLayout>
  );
}
